import { type MouseEvent, useCallback, useRef } from 'react';

import { useEditCanvasStore } from '../../../store';
import { useUndoRedoShortcuts } from '../../shortcuts';
import { commands } from '../commands';
import { useCmdExportDuringDrag } from './useCmdExportDuringDrag';
import { useCommandHistory } from './useCommandHistory';
import { useDuplicateByDragCommand } from './useDuplicateByDragCommand';

export function useCanvasHistory() {
  const undo = useCommandHistory((state) => state.undo);
  const redo = useCommandHistory((state) => state.redo);
  const execute = useCommandHistory((state) => state.execute);
  const nodes = useEditCanvasStore((state) => state.nodes);
  const { onDragStart: onAltDragStart, onDragStop: onAltDragStop } = useDuplicateByDragCommand();
  const {
    isExportActive,
    onDrag: onCmdExportDrag,
    onDragStart: onCmdExportDragStart,
    onDragStop: onCmdExportDragStop,
    renderBadge: cmdExportBadge,
  } = useCmdExportDuringDrag();

  useUndoRedoShortcuts();

  // Track node positions at drag start for undo/redo
  const dragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Track whether alt was held at drag start so CMD export hook can skip when alt-duplicate is active
  const altDragActiveRef = useRef(false);

  const onNodeDragStart = useCallback(
    (event: MouseEvent, _node: unknown, nodes: { id: string; position: { x: number; y: number } }[]) => {
      const positions = new Map<string, { x: number; y: number }>();
      for (const node of nodes) {
        positions.set(node.id, { ...node.position });
      }
      dragStartPositionsRef.current = positions;

      onAltDragStart(event, nodes);
      altDragActiveRef.current = !!event.altKey;
      onCmdExportDragStart(event);
    },
    [onAltDragStart, onCmdExportDragStart],
  );

  const onNodeDrag = useCallback(
    (_event: MouseEvent, _node: unknown, nodes: { id: string; position: { x: number; y: number } }[]) => {
      onCmdExportDrag(_event, nodes, altDragActiveRef.current);
    },
    [onCmdExportDrag],
  );

  const onNodeDragStop = useCallback(
    (event: MouseEvent, _node: unknown, nodes: { id: string; position: { x: number; y: number } }[]) => {
      const previousPositions = Array.from(dragStartPositionsRef.current.entries()).map(([id, position]) => ({
        id,
        position,
      }));
      const newPositions = nodes.map((n) => ({ id: n.id, position: { ...n.position } }));

      // CMD+export takes highest priority. Returns committed positions if handled.
      const committedPositions = onCmdExportDragStop(event, nodes);

      if (committedPositions) {
        // Record the partial move (drag-start → where CMD was pressed)
        if (previousPositions.length > 0) {
          execute(commands.moveNodes(previousPositions, committedPositions));
        }
      } else {
        // Alt+drag duplicate takes priority over regular move
        const altHandled = onAltDragStop(nodes, previousPositions);

        if (!altHandled && previousPositions.length > 0) {
          execute(commands.moveNodes(previousPositions, newPositions));
        }
      }

      dragStartPositionsRef.current = new Map();
      altDragActiveRef.current = false;
    },
    [execute, onAltDragStop, onCmdExportDragStop],
  );

  const readOnly = useEditCanvasStore((state) => state.readOnly);

  const onNodesDelete = useCallback(
    (deletedNodes: { id: string }[]) => {
      if (readOnly) return;
      const deletedIds = new Set(deletedNodes.map((n) => n.id));
      const nodesToDelete = nodes.filter((obj) => deletedIds.has(obj.id));
      if (nodesToDelete.length > 0) {
        execute(commands.removeNodes(nodesToDelete));
      }
    },
    [readOnly, execute, nodes],
  );

  return {
    cmdExportBadge,
    isExportActive,
    onNodeDrag,
    onNodeDragStart,
    onNodeDragStop,
    onNodesDelete,
    onRedo: redo,
    onUndo: undo,
  };
}
